import ElementAndSubtreeObserver, {
	ElementAndSubtreeObserverSortingFunction,
	ObserverEntry,
	ProcessedEvent
} from "../scripts/ElementAndSubtreeObserver";

import ComponentClass from "./ComponentClass";
import StyleClassComponentClass, {isStyleClassComponentClass} from "./StyleClassComponentClass";
import TagNameComponentClass, {isTagNameComponentClass} from "./TagNameComponentClass";
import NamespaceUriComponentClass, {isNamespaceUriComponentClass} from "./NamespaceUriComponentClass";

import ComponentDefinition from "./ComponentDefinition";
import EventManager from "../components/EventManager";
import DependencyManager from "./DependencyManager";

export interface ComponentEvent {
	element: Element;
	component: any;
}

export interface ComponentDefinitionEvent {
	componentClass: ComponentClass<any, any>;
	componentDefinition: ComponentDefinition<any, any>;
}

// The `@ComponentManager.on("removed")` decorators are called before the `componentManager.addEventListener("removed", ...)` listeners.
// The decorator version's method is only called for that component's "removed" event, while the "addEventListener" is called for every
// components "removed" event.
export interface ComponentEventMapStaticMethods {
	// Event triggered before the first instance of each ComponentClass is created.
	// This provides a way to easily do setup work on the static class, but do it "lazy" -- only when needed.
	// When used as an annotation, this is goes on a static class method.
	"beforeFirstConstruction": ComponentDefinitionEvent;
}
export interface ComponentEventMap extends ComponentEventMapStaticMethods {
	// Event takes place immediately after each component is constructed. A component is constructed when a
	// matching a node is attached to the document and the resulting mutation event is triggered by the browser.
	"added": ComponentEvent;
	// Event takes place after all components in the mutation records for a mutation event have been processed,
	// but do not wait for document to be loaded and parsed (aka DOMContentLoaded event).
	"constructed": ComponentEvent;
	// Event takes place after all components in the mutation records for a mutation event have been processed,
	// and after making makes sure that the document has been completely loaded and parsed (see DOMContentLoaded event).
	"ready": ComponentEvent;
	// Event takes place after each component is detached from the detached element.
	"removed": ComponentEvent;
}

const staticOfComponentEventMap = ["beforeFirstConstruction"];
export function isComponentEventStatic(eventType: keyof ComponentEventMap): eventType is keyof ComponentEventMapStaticMethods {
	return staticOfComponentEventMap.indexOf(eventType) !== -1
}

/**
 *
 */
export default class ComponentManager {

	private static readonly instances: WeakMap<Document, ComponentManager> = new WeakMap<Document, ComponentManager>();

	private static readonly elementAndSubtreeObserverSortingFunction: ElementAndSubtreeObserverSortingFunction<OEMetadata>
		= (a: ObserverEntry<any, OEMetadata>, b: ObserverEntry<any, OEMetadata>) => {
		const aMetadata = a.getMetadata(), bMetadata = b.getMetadata();
		if (aMetadata === undefined || bMetadata === undefined) {
			return 0;
		}
		const aDependsOnB = aMetadata.dependencies.indexOf(bMetadata.componentClass) !== -1;
		const bDependsOnA = bMetadata.dependencies.indexOf(aMetadata.componentClass) !== -1;
		if (aDependsOnB) { // If a depends on b
			return bDependsOnA ? 0 : 1; // 0 if they are mutual dependents
		} else if (bDependsOnA) { // If b depends on a
			return -1;
		} else { // If there is no relationship
			return 0;
		}
	};

	private readonly dependencyManager = new DependencyManager<ComponentClass<any, any>>(
		(componentClass: ComponentClass<any, any>) => {
			// =========== Register ===========
			if (isStyleClassComponentClass(componentClass)) {
				this.registerStyleClass(componentClass);
			}
			if (isTagNameComponentClass(componentClass)) {
				this.registerTagName(componentClass);
			}
		},
		item => ComponentDefinition.getComponentDefinition(item) === undefined,
		((item) => isStyleClassComponentClass(item) || isTagNameComponentClass(item)) as ((item: any) => item is ComponentClass<any, any>));

	private readonly elementAndSubtreeObserver: ElementAndSubtreeObserver<OEMetadata>;

	private readonly eventManager = new EventManager<ComponentEventMap>();
	public readonly addEventListener = this.eventManager.addEventListener;
	public readonly removeEventListener = this.eventManager.removeEventListener;

	// ================= Class and Method Decorators =============================
	/**
	 * Class decorator to associate a JS class with HTML style classes and automatically
	 * create and destroy the class when a matching element exists.
	 * Your class must implement one of the following: StyleClassComponent, TagNameComponent or NamespaceUriComponent
	 */
	static register(options?: {requires?: () => (ComponentClass<any, any> | ComponentClass<any, any>[])}) {
		return function(
			componentClass: StyleClassComponentClass<any, any> | TagNameComponentClass<any, any> | NamespaceUriComponentClass<any, any>
		) {
			ComponentManager.getInstance().dependencyManager.registerItem(componentClass, options?.requires);
		}
	}

	/**
	 * This is a method decorator which can be used to cause instance methods
	 * to be called at specific events.
	 * Except for "beforeFirstConstruction", this annotation is used on instance methods.
	 * The "beforeFirstConstruction" type is used on a static class methods.
 	 */
	static on<E extends keyof ComponentEventMap>(eventType: E) {
		return function(
			target: E extends keyof ComponentEventMapStaticMethods ? Function : any, // TODO @Michael: What is the type for an instance member decorator? I couldn't figure it out, if there is one we can make typing give a warning if you try to use, for example, "removed" on a static method.
			methodName: string,
			descriptor?: PropertyDescriptor
		) {
			// target = Either the constructor function of the class when decorator is used on a static member, or the prototype of the class for an instance member.
			if (typeof target === "function") {
				if (isComponentEventStatic(eventType)) {
					ComponentDefinition.addEventMethodName(target.prototype.constructor as ComponentClass<any, any>, eventType, methodName);
				} else {
					throw new Error(
						`Invalid use of ComponentManager.on("${eventType}") decorator on an static member, "${methodName}". ` +
						`When using this decorator for this event type it must be used on an instance method of a class.`
					);
				}
			} else {
				if (isComponentEventStatic(eventType)) {
					throw new Error(
						`Invalid use of ComponentManager.on("${eventType}") decorator on an instance member, "${methodName}". ` +
						`When using this decorator for this event type it must be used on a static method of a class.`
					);
				} else {
					ComponentDefinition.addEventMethodName(target.constructor as ComponentClass<any, any>, eventType, methodName);
				}
			}
		};
	}

	// ================= Static =============================
	static getInstance(node: Node = window.document): ComponentManager {
		const doc: Document = node.nodeName === "#document" ? node as Document : (node.ownerDocument ?? window.document);
		let manager = ComponentManager.instances.get(doc);
		if (manager === undefined) {
			manager = new ComponentManager(doc.documentElement);
			ComponentManager.instances.set(doc, manager);
		}
		return manager;
	}

	// ================= Non-Static =============================

	private constructor(rootElement: Element) {
		this.elementAndSubtreeObserver = ElementAndSubtreeObserver.getInstance(rootElement);
		this.elementAndSubtreeObserver.setSortingFunction(ComponentManager.elementAndSubtreeObserverSortingFunction);
		this.elementAndSubtreeObserver.addEventListener("processed", this.onMutationProcessed);
	}

	private readonly onMutationProcessed = (data: ProcessedEvent) => {
		// Event: "ready"
		data.added.forEach((components, element) => {
			for (let i = 0; i < components.length; i++) {
				let component = components[i];
				if (component !== null && component !== undefined) {
					const componentDefinition = ComponentDefinition.getComponentDefinition(component);
					if (componentDefinition !== undefined) {
						ComponentDefinition.callMethodsForEvent("constructed", componentDefinition, component);
						this.eventManager.notifyEventListeners("constructed", {element, component});

						const doc = element.ownerDocument;
						if (doc.readyState === "interactive" || doc.readyState === "complete") {
							ComponentDefinition.callMethodsForEvent("ready", componentDefinition, component);
							this.eventManager.notifyEventListeners("ready", {element, component});
						} else {
							doc.addEventListener("DOMContentLoaded", () => {
								// Make sure element is still attached to the document before notifying listeners
								// since it is possible that the component was destroyed
								if (element.ownerDocument === doc) {
									ComponentDefinition.callMethodsForEvent("ready", componentDefinition, component);
									this.eventManager.notifyEventListeners("ready", {element, component});
								}
							});
						}
					}
				}
			}
		});
	}

	private generateMetadata(componentClass: ComponentClass<any, any>): OEMetadata {
		return {
			componentClass: componentClass,
			dependencies: this.dependencyManager.getOrCreateDependencyListForItem(componentClass)
		};
	}

	private registerStyleClass<CE extends Element, CC extends StyleClassComponentClass<CE, any>>(componentClass: CC) {
		// Determine styleClass
		const styleClass = componentClass.componentStyleClass;
		if (styleClass === undefined) {
			throw new Error(`Component failed to register: The JavaScript class does not have "componentStyleClass" defined.`);
		}

		// Create a component Definition
		const componentDefinition = new ComponentDefinition<CE, CC>(componentClass, this.dependencyManager.getOrCreateDependencyListForItem(componentClass));

		// Init and destroy instance upon addition of elements with style being added or removed from document
		this.elementAndSubtreeObserver.onElementWithStyleClass<CE>(
			styleClass,
			element => ComponentManager.createComponentInstance(this, componentDefinition, element),
			element => ComponentManager.destroyComponentInstance(this, componentDefinition, element),
			this.generateMetadata(componentClass)
		);
	}

	private registerTagName<CE extends Element, CC extends TagNameComponentClass<CE, any>|NamespaceUriComponentClass<CE, any>>(componentClass: CC) {
		// Determine styleClass
		const tagName = componentClass.componentTagName;
		if (tagName === undefined) {
			throw new Error(`Component failed to register: The JavaScript class does not have "componentTagName" defined.`);
		}

		// Create a component Definition
		const componentDefinition = new ComponentDefinition<CE, CC>(componentClass, this.dependencyManager.getOrCreateDependencyListForItem(componentClass));

		// Init and destroy instance upon addition of elements with style being added or removed from document
		if (isNamespaceUriComponentClass(componentClass)) {
			// Setup with tag name and namespace
			const namespaceUri = componentClass.componentNamespaceUri;
			this.elementAndSubtreeObserver.onElementWithTagNameNS<CE>(
				namespaceUri,
				tagName,
				element => ComponentManager.createComponentInstance(this, componentDefinition, element),
				element => ComponentManager.destroyComponentInstance(this, componentDefinition, element),
				this.generateMetadata(componentClass)
			);
		} else {
			// Setup with tag name
			this.elementAndSubtreeObserver.onElementWithTagName<CE>(
				tagName,
				element => ComponentManager.createComponentInstance(this, componentDefinition, element),
				element => ComponentManager.destroyComponentInstance(this, componentDefinition, element),
				this.generateMetadata(componentClass)
			);
		}
	}

	/**
	 * Listeners will not be notified of the passed element for add and/or remove events.
	 * This does not affect "mutation" listeners though; they will still be notified of mutation.
	 * This method only marks the passed element. It does NOT mark all descendant Elements as well.
	 * If you want to mark all descendants too, see ignoreElementAndDescendants().
	 * This simply calls ignoreElement() on the ElementAndSubtreeObserver which Component manager uses.
	 */
	public ignoreElement(element: Element, ignoreAdd: boolean, ignoreRemove: boolean) {
		this.elementAndSubtreeObserver.ignoreElement(element, ignoreAdd, ignoreRemove);
	}
	/**
	 * Same as ignoreElement() but affects all descendant Elements as well.
	 */
	public ignoreElementAndDescendants(element: Element, ignoreAdd: boolean, ignoreRemove: boolean) {
		this.elementAndSubtreeObserver.ignoreElementAndDescendants(element, ignoreAdd, ignoreRemove);
	}


	public destroy() {
		this.elementAndSubtreeObserver.destroy();
		this.eventManager.destroy();
		throw new Error("Method not implemented.");
	}

	/**
	 * Searches the DOM ancestors of the `contextElement` for instances of the component class which is passed as the second argument.
	 * If the optional "querySelector" is not provided, the style class or tag name defined by the ComponentClass
	 * will be used to query the context.
	 */
	public static queryForAllComponents<CE extends Element, CC extends ComponentClass<CE, InstanceType<CC>>>(
		contextElement: CE, componentClass: CC, querySelector?: string
	): Array<InstanceType<CC>> {
		const components = new Array<InstanceType<CC>>();

		if (contextElement !== null) {
			if (querySelector !== undefined) {
				const queriedElements = contextElement.querySelectorAll<CE>(querySelector);
				queriedElements.forEach(element => {
					const component = ComponentManager.getComponent(element, componentClass);
					if (component !== undefined) {
						components.push(component);
					}
				});

				return components
			}

			const {elementsForStyleClass, elementsForTag} = ComponentManager.findElementsForComponentClass(contextElement, componentClass);
			ComponentManager.processElementsIntoArray<CE, CC>(elementsForStyleClass, componentClass, components);
			ComponentManager.processElementsIntoArray<CE, CC>(elementsForTag, componentClass, components);
		}

		return components;
	}

	/**
	 * Searches the DOM ancestors of the `contextElement` for the first instance of the component class.
	 * This is similar to queryForAllComponents(...) but only returns the first one.
	 *
	 * If the optional "querySelector" is not provided, the style class or tag name defined by the ComponentClass
	 * will be used to query the context.
	 *
	 * Will return null if an instance is not found.
	 */
	public static queryForComponent<CE extends Element, CC extends ComponentClass<CE, InstanceType<CC>>, T extends InstanceType<CC>>(
		contextElement: CE, componentClass: CC, querySelector?: string
	): T | undefined {
		if (querySelector !== undefined) {
			const queriedElement = contextElement.querySelector<CE>(querySelector);
			if (queriedElement === null) {
				return undefined;
			}
			return this.getComponent(queriedElement, componentClass);
		}

		const {elementsForStyleClass, elementsForTag} = ComponentManager.findElementsForComponentClass(contextElement, componentClass);

		if (
			(elementsForStyleClass === null || elementsForStyleClass.length === 0)
			&& (elementsForTag === null || elementsForTag.length === 0)
		) {
			return undefined;
		}

		let firstElem;
		if (elementsForStyleClass === null || elementsForStyleClass.length === 0) {
			if (elementsForTag !== null) {
				firstElem = elementsForTag[0];
			} else {
				throw "Impossible null";
			}
		} else if (elementsForTag === null || elementsForTag.length === 0) {
			firstElem = elementsForStyleClass[0];
		} else {
			const elemForStyleClass = elementsForStyleClass[0]!;
			const elemForTag = elementsForTag[0]!;
			firstElem = elemForStyleClass.compareDocumentPosition(elemForTag) & Node.DOCUMENT_POSITION_FOLLOWING
				? elemForStyleClass
				: elemForTag;
		}

		return ComponentManager.getComponent(firstElem as CE, componentClass);
	}

	/**
	 * Returns all initialized components for the element.
	 * If no components are attached to the element, an empty array is returned.
	 */
	public static getComponents(elem: Element): any[] {
		return ComponentDefinition.getInstances(elem);
	}

	/**
	 * Gets the component instances for the element.
	 * If no component is attached to the element, undefined is returned.
	 */
	public static getComponent<CE extends Element, CC extends ComponentClass<CE, InstanceType<CC>>, T extends InstanceType<CC>>(
		element: CE, componentClass: CC, includeClassExtenders: boolean = true
	): T | undefined {
		const componentDefinition = ComponentDefinition.getComponentDefinition(componentClass);
		if (componentDefinition === undefined) {
			throw new Error(`Component class is not registered and therefore there can not be any instances registered on any components.`);
		}
		return componentDefinition.getInstance(element, includeClassExtenders);
	}

	private static findElementsForComponentClass<CE extends Element>(contextElement: CE, componentClass: ComponentClass<CE, any>): ElementsForTagAndStyleClass {
		let elementsForStyleClass = null;
		if (isStyleClassComponentClass(componentClass)) {
			const styleClass = componentClass.componentStyleClass;
			elementsForStyleClass = contextElement.getElementsByClassName(styleClass);
		}

		let elementsForTag = null;
		if (isTagNameComponentClass(componentClass)) {
			const tagName = componentClass.componentTagName;
			if (isNamespaceUriComponentClass(componentClass)) {
				const tagNs = componentClass.componentNamespaceUri;
				elementsForTag = contextElement.getElementsByTagNameNS(tagNs, tagName);
			} else {
				elementsForTag = contextElement.getElementsByTagName(tagName);
			}
		}
		return {elementsForStyleClass, elementsForTag};
	}

	private static processElementsIntoArray<CE extends Element, CC extends ComponentClass<CE, InstanceType<CC>>>(
		elements: HTMLCollectionOf<Element> | null, componentClass: CC, componentsMap: Array<InstanceType<CC>>
	) {
		if (elements !== null && elements.length > 0) {
			for (let i = 0; i < elements.length; i++) {
				const elem = elements[i];
				const component = ComponentManager.getComponent(elem as CE, componentClass);
				if (component !== undefined) {
					componentsMap.push(component);
				}
			}
		}
	}

	public static getComponentNameForLogging<CE extends Element, CC extends ComponentClass<CE, InstanceType<CC>>>(componentClassOrInstance: CC| {}): string {
		let displayValue = [];
		let componentClass: any;
		if (typeof componentClassOrInstance === "object") {
			componentClass = componentClassOrInstance.constructor;
		} else if (typeof componentClassOrInstance === "function") {
			componentClass = componentClassOrInstance;
		}
		if (isStyleClassComponentClass(componentClass)) {
			displayValue.push(componentClass.componentStyleClass);
		}
		if (isNamespaceUriComponentClass(componentClass)) {
			displayValue.push(componentClass.componentNamespaceUri);
		}
		if (isTagNameComponentClass(componentClass)) {
			displayValue.push(componentClass.componentTagName);
		}
		return displayValue.join(" ");
	}


	/**
	 * While components are automatically initialized when on DOM mutations, this method provides you a way
	 * to cause a specific component to be initialized on an element without a DOM mutation.
	 * For example, while you could component's style class to an element and than cause DOM mutation to trigger
	 * the creation, this would cause a lot extra work. Instead, this method allows you to dynamically initialize
	 * a component on an element which did not previously have the component's style class (or other criteria).
	 * If the component class is a style StyleClassComponentClass (aka has a "componentStyleClass" attribute),
	 * this method will also add the the style class to the element.
	 *
	 * @param componentClass
	 * @param element
	 */
	createComponentInstance<CE extends Element, CC extends ComponentClass<CE, InstanceType<CC>>>(componentClass: CC, element: CE): InstanceType<CC> | null {
		const componentDefinition = ComponentDefinition.getComponentDefinition<CE, CC>(componentClass);
		if (componentDefinition === undefined) {
			return null;
		}
		if (isStyleClassComponentClass(componentClass)) {
			element.classList.add(componentClass.componentStyleClass);
		}
		return ComponentManager.createComponentInstance(this, componentDefinition, element);
	}
	destroyComponentInstance<CE extends Element, CC extends ComponentClass<CE, InstanceType<CC>>>(componentClass: CC, element: CE): InstanceType<CC> | null {
		const componentDefinition = ComponentDefinition.getComponentDefinition<CE, CC>(componentClass);
		if (componentDefinition === undefined) {
			return null;
		}
		if (isStyleClassComponentClass(componentClass)) {
			element.classList.remove(componentClass.componentStyleClass);
		}
		return ComponentManager.destroyComponentInstance(this, componentDefinition, element);
	}

	private static createComponentInstance<CE extends Element, CC extends ComponentClass<CE, InstanceType<CC>>>(
		componentManager: ComponentManager, componentDefinition: ComponentDefinition<CE, CC>, element: CE
	): InstanceType<CC> | null {
		return componentDefinition.newInstance(element, componentManager.eventManager);
	}

	private static destroyComponentInstance<CE extends Element, CC extends ComponentClass<CE, InstanceType<CC>>>(
		componentManager: ComponentManager, componentDefinition: ComponentDefinition<CE, CC>, element: CE
	): InstanceType<CC> | null {
		return componentDefinition.destroyInstance(element, componentManager.eventManager);
	}

}
type ElementsForTagAndStyleClass = {elementsForStyleClass: HTMLCollectionOf<Element> | null, elementsForTag: HTMLCollectionOf<Element> | null};

export type DependencyList = ComponentClass<any, any>[];
type OEMetadata = {
	readonly componentClass: ComponentClass<any, any>,
	readonly dependencies: DependencyList
}
